"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRailCapacity = void 0;
const react_1 = __importDefault(require("react"));
const util_1 = require("@app/util");
/**
 * How many of the collapsed rail's shortcut icons the viewport has room for.
 *
 * The rail is 44px wide and stacks the expand control, a separator, the shortcuts, the vertical
 * label and the logo. It advertises EVERY quick action rather than the design's
 * first four, and the set is the business's own configuration — so its length is not something
 * this code gets to know. On a short viewport, or with a generously configured strip, the list
 * has to give way.
 *
 * **It gives way by showing fewer WHOLE icons — never a clipped one, and never a scrollbar.**
 * That choice follows from what a rail icon does: it expands the sidebar and nothing more, the
 * same as every other icon in the rail (see `manager-assistant-column.tsx`). Scrolling a 44px
 * column to reach an icon that does exactly what the icon above it does would be an affordance
 * that pays nothing back, and half a glyph reads as a rendering bug rather than as "there is
 * more". The expand control at the top of the rail is always visible and is the honest route to
 * the shortcuts that did not fit.
 *
 * The count is measured rather than derived from the stylesheet's numbers because every one of
 * them is in `rem`, and `rem` is 14, 15, 16 or 20px here (`app/style/const.scss`) — and because
 * the space left over also depends on how long the rail's label reads in the manager's language.
 */
const useRailCapacity = () => {
    // Everything, until the first measurement — which runs before the browser paints, so a tall
    // viewport (every viewport the design's eight fit on) never flickers through a shorter rail.
    const [railCapacity, setRailCapacity] = react_1.default.useState(Number.POSITIVE_INFINITY);
    const observerRef = react_1.default.useRef(null);
    // One icon plus the gap below it, remembered across measurements: once the count reaches zero
    // there is no icon left to measure, and without this the rail could never grow back.
    const pitchRef = react_1.default.useRef(0);
    const railActionsRef = react_1.default.useCallback((node) => {
        if ((0, util_1.isValue)(observerRef.current)) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (!(0, util_1.isValue)(node))
            return;
        const measure = () => {
            const icon = node.firstElementChild;
            if ((0, util_1.isValue)(icon)) {
                pitchRef.current =
                    icon.getBoundingClientRect().height +
                        parseFloat(window.getComputedStyle(icon).marginBottom);
            }
            const pitch = pitchRef.current;
            if (!Number.isFinite(pitch) || pitch <= 0)
                return;
            // The list is the flex child that takes whatever the rail's furniture leaves it, so its
            // height does not move with the number of icons in it — measuring cannot feed back into
            // what is being measured. The bottom-most icon's own gap is counted as if it had to fit,
            // which costs at most one icon and keeps this to a single number.
            setRailCapacity(Math.floor(node.clientHeight / pitch));
        };
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        measure();
        observerRef.current = observer;
    }, []);
    return { railActionsRef, railCapacity };
};
exports.useRailCapacity = useRailCapacity;
exports.default = exports.useRailCapacity;
